import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import SideBar from "../components/SideBar";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AddUser() {
  const { t, i18n } = useTranslation();
  const [isSideBarOpen, setIsSideBarOpen] = useState(true);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false); // حالة فتح المودال
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("الاسم مطلوب"),
    username: Yup.string().required("اسم المستخدم مطلوب"),
    password: Yup.string().required("كلمة المرور مطلوبة"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "كلمات المرور يجب أن تتطابق")
      .required("تأكيد كلمة المرور مطلوب"),
    role: Yup.string().required("الرجاء اختيار دور المستخدم"),
    branch: Yup.string().required("الرجاء اختيار الفرع"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset, // لإعادة تعيين النموذج
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        const response = await axios.get(
          "https://tarabotcs.com-eg.net/api/Users/V1/GetRoles",
          {
            headers: {
              CompanyID: userData.Data.CompanyID,
              AccessToken: userData.Data.AccessToken,
            },
          }
        );
        setRoles(response.data.Data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Session expired, logging out...");
          localStorage.removeItem("userData");
          navigate("/");
        } else {
          console.error("خطأ في جلب الأدوار:", error);
        }
      }
    };

    fetchRoles();
  }, [navigate]);
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        const response = await axios.get(
          "https://tarabotcs.com-eg.net/api/Users/V1/GetBranches",
          {
            headers: {
              CompanyID: userData.Data.CompanyID,
              AccessToken: userData.Data.AccessToken,
            },
          }
        );
        setBranches(response.data.Data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Session expired, logging out...");
          localStorage.removeItem("userData");
          navigate("/");
        } else {
          console.error("خطأ في جلب الأدوار:", error);
        }
      }
    };

    fetchBranches();
  }, [navigate]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      await axios.post(
        "https://tarabotcs.com-eg.net/api/Users/V1/AddUser",
        {
          UserID: 0,
          UserName: data.username,
          UserNo: "",
          DispalyName: data.name,
          Password: data.password,
          RoleID: data.role,
          IsLocked: isLocked,
          Notes: "",
        },
        {
          headers: {
            CompanyID: "221005",
            AccessToken: userData.Data.AccessToken,
          },
        }
      );
      setLoading(false);
      setIsDialogOpen(true); // فتح المودال عند نجاح إضافة المستخدم
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("Session expired, logging out...");
        localStorage.removeItem("userData");
        navigate("/");
      } else {
        console.error("خطأ في إضافة المستخدم:", error);
      }
      setLoading(false);
    }
  };

  const handleResetForm = () => {
    reset(); // تفريغ النموذج
    setIsDialogOpen(false); // إغلاق المودال
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false); // إغلاق المودال فقط
  };
  const isRtl = i18n.language === "ar";
  return (
    <Box sx={{ display: "flex" }}>
      <SideBar
        open={isSideBarOpen}
        toggleOpen={() => setIsSideBarOpen(!isSideBarOpen)}
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100%",
          }}
        >
          <CircularProgress size={80} />
        </Box>
      ) : (
        <Box
          sx={{
            height: "calc(100vh - 100px)",
            width: "95%",
            marginTop: "70px",
            marginLeft: isSideBarOpen
              ? i18n.language === "ar"
                ? "250"
                : "5px"
              : i18n.language === "ar"
              ? "250px"
              : "5px",
            marginRight: "10px",
            transition: "margin-left 0.3s",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              marginBottom: "20px",
              fontWeight: "bold",
              color: "#263150",
            }}
          >
            {t("addUser")}
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label={t("name")}
              fullWidth
              {...register("name")}
              error={!!errors.name}
              helperText={errors.name?.message}
              margin="normal"
              variant="outlined"
            />

            <TextField
              label={t("userName")}
              fullWidth
              {...register("username")}
              error={!!errors.username}
              helperText={errors.username?.message}
              margin="normal"
              variant="outlined"
            />

            <TextField
              label={t("password")}
              fullWidth
              type="password"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
              margin="normal"
              variant="outlined"
            />

            <TextField
              label={t("confirmPassword")}
              fullWidth
              type="password"
              {...register("confirmPassword")}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              margin="normal"
              variant="outlined"
            />

            <TextField
              select
              label={t("selectRole")}
              fullWidth
              {...register("role")}
              error={!!errors.role}
              helperText={errors.role?.message}
              margin="normal"
              variant="outlined"
            >
              {roles.map((role) => (
                <MenuItem key={role.RoleID} value={role.RoleID}>
                  {role.EnglishName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t("selectBranch")}
              fullWidth
              {...register("branch")}
              error={!!errors.branch}
              helperText={errors.branch?.message}
              margin="normal"
              variant="outlined"
            >
              {branches.map((branch) => (
                <MenuItem key={branch.BranchID} value={branch.BranchID}>
                  {branch.EnglishName}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={isLocked}
                  onChange={(e) => setIsLocked(e.target.checked)}
                  color="primary"
                />
              }
              label={t("isLocked")}
              sx={{ marginTop: "20px" }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#2563eb",
                marginTop: "20px",
                padding: "11px",
                borderRadius: "8px",
                fontWeight: 700,
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            >
              {t("save")}
            </Button>
          </form>

          {/* المودال */}
          <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
            <DialogTitle sx={{ fontWeight: 700 }}>{t("success")}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t("userAddedSuccessfully")}
              </DialogContentText>
            </DialogContent>
            <DialogActions
              sx={{ display: "flex", justifyContent: "center", gap: "10px", pb: 2 }}
            >
              <Button
                onClick={handleResetForm}
                variant="contained"
                sx={{
                  backgroundColor: "#2563eb",
                  color: "white",
                  borderRadius: "8px",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#1d4ed8" },
                }}
              >
                {t("addUser")}
              </Button>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{
                  borderColor: "#cbd5e1",
                  color: "#64748b",
                  borderRadius: "8px",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#94a3b8" },
                }}
              >
                {t("cancel")}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
}
